import {useFlags, useLDClient} from 'launchdarkly-react-client-sdk';
import {useState, useEffect, useRef, MutableRefObject} from 'react';
import getLtid from '@/utils/getLtid';
import useUserQuery from '../queries/use-user-query';
import useToken from '../use-token';
import Sentry from '@/services/sentry/Sentry';

const TIMEOUT_DURATION_IN_SECONDS = 5;

function clearTimeoutRef(
  timeoutRef: MutableRefObject<NodeJS.Timeout | null> | null
) {
  if (timeoutRef?.current) {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }
}

function useSetLaunchDarklyContext() {
  const {isAuthenticated: isTokenAuthenticated, initialized: tokenInitialized} =
    useToken(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const flags = useFlags();
  const ldClient = useLDClient();
  const ltId = getLtid();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isAuthenticated = isTokenAuthenticated && !!ltId;
  const {user} = useUserQuery({
    overrideEnabled: isAuthenticated,
    include: [],
  });

  // Timeout to prevent endless loading
  // If LaunchDarkly fails to initialize, we falsely mark it as "initialized"
  // so the app can continue. This means:
  // - useFlags() will return empty object {} or default values
  // - Feature flags will be undefined/false, causing fallback behavior
  // - Users get default experience instead of hanging on loader
  // This is acceptable trade-off: working app with missing features > broken app
  useEffect(() => {
    if (!isInitialized) {
      timeoutRef.current = setTimeout(() => {
        setIsInitialized(true);
        Sentry.addBreadcrumb({
          type: 'info',
          category: 'launchdarkly',
          message: 'LaunchDarkly initialization timed out',
          level: 'warning',
        });
      }, TIMEOUT_DURATION_IN_SECONDS * 1000);
    }

    return () => {
      clearTimeoutRef(timeoutRef);
    };
  }, [isInitialized]);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    if (
      isAuthenticated &&
      tokenInitialized &&
      user?.email &&
      ltId &&
      ldClient?.getContext()?.key !== ltId
    ) {
      let name;
      if (!user?.firstName && !user?.lastName) {
        name = user?.email;
      } else {
        name = `${user?.firstName} ${user?.lastName}`;
      }

      ldClient
        .identify({
          key: ltId,
          kind: 'cwa-user',
          name,
          first_name: user?.firstName,
          last_name: user?.lastName,
          email: user?.email,
          ip: user?.currentSignInIp,
        })
        .then(() => {
          setIsInitialized(true);
          clearTimeoutRef(timeoutRef);
        })
        .catch((error) => {
          // LaunchDarkly failed - log it and let app continue
          Sentry.addBreadcrumb({
            type: 'error',
            category: 'launchdarkly',
            message: 'LaunchDarkly identify failed',
            level: 'error',
            data: {
              error: error.message,
              ltId,
              isAuthenticated,
            },
          });
          setIsInitialized(true);
          clearTimeoutRef(timeoutRef);
        });
    } else if (
      !isAuthenticated &&
      ldClient?.getContext()?.key !== 'anonymous-user'
    ) {
      ldClient
        .identify({key: 'anonymous-user', anonymous: true})
        .then(() => {
          setIsInitialized(true);
          clearTimeoutRef(timeoutRef);
        })
        .catch((error) => {
          Sentry.addBreadcrumb({
            type: 'error',
            category: 'launchdarkly',
            message: 'LaunchDarkly identify failed',
            level: 'error',
          });
        });
    }

    if (tokenInitialized && !isAuthenticated && !isInitialized) {
      setIsInitialized(true);
      clearTimeoutRef(timeoutRef);
    }
  }, [isAuthenticated, tokenInitialized, user, isInitialized, ltId, ldClient]);

  return {
    flags,
    launchDarklyInitialized: isInitialized,
  };
}

export default useSetLaunchDarklyContext;
